import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, Space, Typography, Tooltip, message } from 'antd';
import {
  EnvironmentOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  AimOutlined
} from '@ant-design/icons';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const { Text } = Typography;

const MapPicker = ({ onSelect, initialLat = 39.9334, initialLng = 32.8597, showInitialMarker = false, isView = false }) => {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markerRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const initMap = () => {
      if (mapRef.current) {
        return;
      }

      if (!containerRef.current) {
        return;
      }

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      try {
        mapRef.current = L.map(containerRef.current, {
          center: [initialLat, initialLng],
          zoom: 6,
          zoomControl: false,
          attributionControl: false,
          doubleClickZoom: true,
          scrollWheelZoom: true,
          dragging: true,
          touchZoom: true,
          boxZoom: true,
          keyboard: true
        });

      } catch (error) {
        return;
      }

      // Harita katmanı - daha güvenilir tile server kullan
      try {
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 18,
          minZoom: 3,
          subdomains: 'abc'
        }).addTo(mapRef.current);
      } catch (error) {
      }

      // Özel zoom kontrolü ekle
      const zoomControl = L.control.zoom({
        position: 'topright',
        zoomInTitle: 'Yakınlaştır',
        zoomOutTitle: 'Uzaklaştır'
      });
      zoomControl.addTo(mapRef.current);

      // Harita tıklama olayı - sadece görüntüleme modunda değilse aktif
      if (!isView) {
        mapRef.current.on('click', (e) => {
          const { lat, lng } = e.latlng;
          updateMarker(lat, lng);
          onSelect({ lat, lng });
          message.success(`Konum seçildi: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        });
      }

      // View değişiklik olaylarını dinle
      mapRef.current.on('viewreset', () => {
        if (mapRef.current) {
          setTimeout(() => {
            mapRef.current.invalidateSize();
          }, 100);
        }
      });

      mapRef.current.on('moveend', () => {
        if (mapRef.current) {
          setTimeout(() => {
            mapRef.current.invalidateSize();
          }, 100);
        }
      });

      mapRef.current.on('zoomend', () => {
        if (mapRef.current) {
          setTimeout(() => {
            mapRef.current.invalidateSize();
          }, 100);
        }
      });

      // Harita yüklendiğinde
      mapRef.current.whenReady(() => {
        setIsMapLoaded(true);
        // Haritayı yeniden boyutlandır
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.invalidateSize();
          }
        }, 100);
        
        // Eğer başlangıç koordinatları varsa marker'ı göster
        if (showInitialMarker) {
          setTimeout(() => {
            updateMarker(initialLat, initialLng);
          }, 200);
        }
      });

      // Resize olayını dinle
      const handleResize = () => {
        if (mapRef.current) {
          mapRef.current.invalidateSize();
        }
      };

      window.addEventListener('resize', handleResize);

      // Cleanup function
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    };

    // Haritayı başlat
    const cleanup = initMap();
    return cleanup;
  }, [onSelect, initialLat, initialLng]);

  // Component unmount olduğunda haritayı temizle
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // showInitialMarker değiştiğinde marker'ı güncelle
  useEffect(() => {
    if (isMapLoaded && showInitialMarker && mapRef.current) {
      updateMarker(initialLat, initialLng);
    }
  }, [showInitialMarker, initialLat, initialLng, isMapLoaded]);

  // Harita yüklendikten sonra boyutlandırma
  useEffect(() => {
    if (isMapLoaded && mapRef.current) {
      const timer = setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.invalidateSize();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isMapLoaded]);

  const updateMarker = (lat, lng) => {
    if (!mapRef.current) return;

    if (markerRef.current) {
      mapRef.current.removeLayer(markerRef.current);
    }

    // Özel marker ikonu
    const customIcon = L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="
          background: #1890ff;
          border: 3px solid white;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="
            background: white;
            border-radius: 50%;
            width: 8px;
            height: 8px;
          "></div>
        </div>
      `,
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });

    markerRef.current = L.marker([lat, lng], { icon: customIcon }).addTo(mapRef.current);

    // Marker'a popup ekle
    markerRef.current.bindPopup(`
      <div style="text-align: center; padding: 8px;">
        <strong>Seçilen Konum</strong><br/>
        Enlem: ${lat.toFixed(6)}<br/>
        Boylam: ${lng.toFixed(6)}
      </div>
    `);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      message.error('Tarayıcınız konum servisini desteklemiyor');
      return;
    }

    message.loading('Konumunuz alınıyor...', 0);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentLocation({ lat: latitude, lng: longitude });

        if (mapRef.current) {
          // Önce haritayı yeniden boyutlandır
          mapRef.current.invalidateSize();

          // Kısa bir gecikme ile view'ı değiştir
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.setView([latitude, longitude], 15, {
                animate: true,
                duration: 1
              });

              // View değişikliği tamamlandıktan sonra marker'ı güncelle
              setTimeout(() => {
                if (mapRef.current) {
                  updateMarker(latitude, longitude);
                  onSelect({ lat: latitude, lng: longitude });

                  // Son olarak tekrar boyutlandır
                  mapRef.current.invalidateSize();
                }
              }, 500);
            }
          }, 100);
        }

        message.destroy();
        message.success('Mevcut konumunuz haritaya eklendi');
      },
      (error) => {
        message.destroy();
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message.error('Konum erişimi reddedildi');
            break;
          case error.POSITION_UNAVAILABLE:
            message.error('Konum bilgisi mevcut değil');
            break;
          case error.TIMEOUT:
            message.error('Konum alma zaman aşımına uğradı');
            break;
          default:
            message.error('Bilinmeyen bir hata oluştu');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const resetMap = () => {
    if (mapRef.current) {
      // Önce haritayı yeniden boyutlandır
      mapRef.current.invalidateSize();

      // Marker'ı temizle
      if (markerRef.current) {
        mapRef.current.removeLayer(markerRef.current);
        markerRef.current = null;
      }

      // Kısa bir gecikme ile view'ı değiştir
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.setView([initialLat, initialLng], 6, {
            animate: true,
            duration: 1
          });

          // View değişikliği tamamlandıktan sonra tekrar boyutlandır
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.invalidateSize();
            }
          }, 500);
        }
      }, 100);

      message.info('Harita varsayılan konuma sıfırlandı');
    }
  };


  return (
    <Card
      style={{
        border: '2px solid #f0f0f0',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
      bodyStyle={{ padding: '0' }}
    >
      {/* Harita Kontrolleri */}
      <div style={{
        padding: '16px',
        backgroundColor: '#fafafa',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <Space direction="vertical" size="small" style={{ flex: 1, minWidth: '200px' }}>
            <Text strong style={{ fontSize: '14px' }}>
              <EnvironmentOutlined style={{ marginRight: '4px' }} />
              {isView ? 'Konum Görüntüleyici' : 'Harita Seçici'}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {isView ? 'Seçili konum gösteriliyor' : 'Haritada bir konum seçmek için tıklayın'}
            </Text>
          </Space>

          {!isView && (
            <Space size="small" style={{ flexWrap: 'wrap' }}>
              <Tooltip title="Mevcut konumunuzu al">
                <Button
                  type="default"
                  icon={<AimOutlined />}
                  onClick={getCurrentLocation}
                  size="small"
                >
                  Konumumu Al
                </Button>
              </Tooltip>
              <Tooltip title="Haritayı varsayılan konuma sıfırla">
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={resetMap}
                  size="small"
                >
                  Sıfırla
                </Button>
              </Tooltip>
            </Space>
          )}
        </div>
      </div>

      {/* Harita Container */}
      <div
        ref={containerRef}
        style={{
          height: '350px',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#f8f9fa',
          border: '1px solid #ddd'
        }}
        className="map-container"
        id="map-container"
      >
        {!isMapLoaded && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 1000
          }}>
            <div style={{
              padding: '20px',
              backgroundColor: 'rgba(255,255,255,0.9)',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <Text>Harita yükleniyor...</Text>
            </div>
          </div>
        )}
      </div>

      {/* Alt Bilgi */}
      {!isView && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f9f9f9',
          borderTop: '1px solid #f0f0f0',
          fontSize: '12px',
          color: '#666'
        }}>
          <Space>
            <InfoCircleOutlined />
            <Text type="secondary">
              Haritada bir yere tıklayarak konum seçebilirsiniz
            </Text>
          </Space>
        </div>
      )}

    </Card>
  );
};

export default MapPicker;